import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import ReactPlayer from "react-player";
import { useNavigate } from "react-router-dom";
import { Context } from "@/components/Store";
import { useBootstrap } from "@/features/bootstrap";
import { useCongratulations } from "./useCongratulations";
import { HelpfulnessRating } from "./components/HelpfulnessRating";
import EndPracticeBrainModal from "./components/EndPracticeBrainModal";
import { useAudioPreference } from "@/hooks/useAudioPreference";
import WellnessProgressTracker from "@/components/WellnessProgressTracker";
import {
  formatActionTextForSentence,
  getOtherWellnessActionText,
  getFormattedOtherWellnessActionText,
  getWellnessActionBackgroundColor,
  getWellnessActionDisplay,
  getWellnessActionImageSrc,
  isOtherWellnessAction,
} from "@/utils/wellnessActions";

export default function Congratulations({ wellnessId, achievements }) {
  const [wellnessDataPoint] = useContext(Context);
  const [playing] = useAudioPreference();
  const { data: user } = useBootstrap();
  const navigate = useNavigate();
  const [showCompletionBrainModal, setShowCompletionBrainModal] =
    useState(false);
  const [showHoverBrainModal, setShowHoverBrainModal] = useState(false);

  const { isSubmitting, submitHelpful, submitHelpfulInBackground } =
    useCongratulations({ wellnessId, achievements });

  const audioPrefix = (user?.voice ?? "english female").replace(" ", "_");
  const chosenEmotion = (wellnessDataPoint?.emotion ?? "").toLowerCase();
  const chosenAction = wellnessDataPoint?.action ?? "";
  const isOtherAction = isOtherWellnessAction(chosenAction);
  const displayAction =
    isOtherAction && getOtherWellnessActionText(chosenAction)
      ? formatActionTextForSentence(getOtherWellnessActionText(chosenAction))
      : formatActionTextForSentence(chosenAction);
  const chosenActionLabel =
    isOtherAction && getFormattedOtherWellnessActionText(chosenAction)
      ? getFormattedOtherWellnessActionText(chosenAction)
      : getWellnessActionDisplay(chosenAction);
  const emotionColorClass = ["sad", "tired", "lonely"].includes(chosenEmotion)
    ? "text-[#1E3A8A]"
    : ["stressed", "scared", "angry"].includes(chosenEmotion)
      ? "text-[#4C1D95]"
      : "text-[#064E3B]";
  const actionImageSrc = wellnessDataPoint?.action
    ? getWellnessActionImageSrc(wellnessDataPoint.action, {
        adultActionImages: user?.adultActionImages,
      })
    : null;
  const enlargedPractices = {
    "Help Others": "70%",
    "Imagine a peaceful place": "68%",
    "Imagine positive things in the future": "65%",
    "Squeeze my fist": "68%",
    "Give someone a high five": "68%",
    "Write in my journal": "68%",
    "Move my body": "68%",
    "Use my growth mindset": "65%",
    "Give someone a compliment": "68%",
    Stretch: "65%",
    "Create art": "68%",
    "Be mindful. Be where my feet are": "59%",
    "Count to ten taking a deep breath each time": "52%",
  };

  const imageSize = Object.keys(enlargedPractices).includes(displayAction)
    ? enlargedPractices[displayAction]
    : "55%";
  const actionBackgroundColor = getWellnessActionBackgroundColor(
    wellnessDataPoint?.action,
    "#4CC68D",
  );
  const [isReflectTitleActive, setIsReflectTitleActive] = useState(false);
  const totalPracticeCount =
    typeof achievements?.user?.count === "number"
      ? achievements.user.count
      : typeof user?.count === "number"
        ? user.count
        : 0;
  const shouldShowEndOfPracticeModal = totalPracticeCount <= 3;

  useEffect(() => {
    const headingTimer = setTimeout(() => {
      setIsReflectTitleActive(true);
    }, 3000);

    return () => clearTimeout(headingTimer);
  }, []);

  function handleHelpfulAnswer(helpful) {
    if (shouldShowEndOfPracticeModal) {
      submitHelpfulInBackground(helpful);
      setShowCompletionBrainModal(true);
      return;
    }

    submitHelpful(helpful);
  }

  return (
    <motion.div
      className="min-h-screen bg-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="h-0 w-0 overflow-hidden opacity-0">
        <ReactPlayer
          playing={playing}
          url={`${process.env.PUBLIC_URL}/audio/${audioPrefix}/congratulations.wav`}
        />
      </div>

      <div className="sticky top-0 mt-10 sm:mt-0 z-10 shrink-0 bg-white sm:ml-[130px] px-0 sm:px-8 lg:px-32">
        <header className="px-2 py-4 text-center sm:pb-4 sm:pt-8">
          <h1 className="mb-2 text-2xl font-bold md:text-3xl">
            {isReflectTitleActive ? "Reflect on Your Practice" : "Nice Work!🎉"}
          </h1>
          <p className="mx-auto text-[15px] sm:text-base text-gray-600">
            Great work! How did it go?
          </p>
        </header>
        <WellnessProgressTracker />
      </div>

      <div className="px-4 pb-8 sm:ml-[130px] sm:px-8 lg:px-32">
        <section className="mx-auto mt-4 w-full">
          <p className="text-center p-5 bg-[#D1D5DB1A] text-base font-medium text-[#374151] sm:text-lg">
            Earlier, you felt{" "}
            <span className={`font-bold ${emotionColorClass}`}>
              {chosenEmotion || "emotion"}.{" "}
            </span>
            Was it helpful to <strong>"{displayAction}"</strong>?
          </p>

          <div className="2xl:my-10 my-6 mx-5 flex justify-center">
            <div
              className="relative flex h-64 w-64 flex-col items-center justify-center overflow-hidden rounded-full text-center text-white 2xl:h-96 2xl:w-96 md:h-72 md:w-72 sm:h-64 sm:w-64"
              style={{ backgroundColor: actionBackgroundColor }}
            >
              {actionImageSrc && (
                <img
                  className="max-h-[65%] max-w-[65%] object-contain"
                  style={{ height: imageSize }}
                  src={actionImageSrc}
                  alt=""
                />
              )}
              <div className="max-w-[65%] px-2.5 pt-2 pb-0 text-center text-sm font-bold line-clamp-3 overflow-hidden sm:text-xl">
                {chosenActionLabel}
              </div>
            </div>
          </div>

          <div>
            <HelpfulnessRating
              practiceAction={wellnessDataPoint?.action}
              isSubmitting={isSubmitting}
              onAnswer={handleHelpfulAnswer}
            />
          </div>

          {!shouldShowEndOfPracticeModal && (
            <div className="relative mt-6 flex justify-center">
              <button
                type="button"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#256AA6] underline decoration-[#256AA6] underline-offset-4"
                onMouseEnter={() => setShowHoverBrainModal(true)}
              >
                <span>What you just did for your brain! 🧠</span>
              </button>

              <EndPracticeBrainModal
                isOpen={showHoverBrainModal}
                showDoneButton={false}
                onClose={() => setShowHoverBrainModal(false)}
                variant="hovercard"
              />
            </div>
          )}
        </section>
      </div>

      <EndPracticeBrainModal
        isOpen={showCompletionBrainModal}
        showDoneButton={true}
        onClose={() => setShowCompletionBrainModal(false)}
        onDone={() =>
          navigate("/studentdashboard", { state: { refreshingData: true } })
        }
      />
    </motion.div>
  );
}
